import type {
    AnalyticsFlightsAndAppSettings,
    AnalyticsFlightsAndEcsSettingsAddon,
} from 'owa-analytics-types';
import type { AccountKey } from 'owa-client-types';
import { getAnalyticsAddon } from 'owa-analytics-addons';
import { trace } from 'owa-trace';
import { getCachedSettings } from './getCachedSettings';

const flightsAndApplicationSettingsCacheByAccountKey = new Map<
    AccountKey | 'globalAccount',
    AnalyticsFlightsAndAppSettings
>();

export function getAnalyticsFlightsAndAppSettings(
    accountKey: AccountKey | 'globalAccount' = 'globalAccount'
): AnalyticsFlightsAndAppSettings | null {
    /**
     * When running tests, we won't pull the actual flights so we need to pass some default ones instead.
     * This will be removed in prod builds.
     */
    if (process.env.JEST_WORKER_ID) {
        return TEST_ONLY_getDefaultSettings();
    }

    // On the main thread, use the local cache if available for speed
    const cachedFlights = flightsAndApplicationSettingsCacheByAccountKey.get(accountKey);
    if (cachedFlights) {
        return cachedFlights;
    }

    // On the workers, use the cached settings if available
    const cachedSettings = getCachedSettings(accountKey);
    if (cachedSettings?.flightsAndEcsSettings) {
        return cachedSettings?.flightsAndEcsSettings;
    }

    // If the settings don't exist, fallback to trying to initialize them (only works on the main thread)
    if (getAnalyticsAddon('AnalyticsFlightsAndEcsSettings').isRegistered) {
        const flights = getAnalyticsAddon<AnalyticsFlightsAndEcsSettingsAddon>(
            'AnalyticsFlightsAndEcsSettings'
        )
            .executeNow()
            ?.initializeAnalyticsFlightsAndAppSettings(accountKey);

        if (flights) {
            flightsAndApplicationSettingsCacheByAccountKey.set(accountKey, flights);
        }
        return flights ?? null;
    }

    trace.warn(
        ' AnalyticsFlightsAndEcsSettingsAddon is not registered. Account Key: ' + accountKey,
        'analytics'
    );

    return null;
}

export function updateFlightsAndApplicationSettingsCache(
    cache: AnalyticsFlightsAndAppSettings,
    accountKey: AccountKey | 'globalAccount' = 'globalAccount'
): void {
    flightsAndApplicationSettingsCacheByAccountKey.set(accountKey, cache);
}

function TEST_ONLY_getDefaultSettings(): AnalyticsFlightsAndAppSettings {
    return {
        disabledDatapoints: [],
        throttledDatapoints: [],
        disableIsCoreDatapoints: [],
        enableHighCountEventThrottle: true,
        highCountEventThrottleWindow: 15,
        highCountEventThrottleThreshold: 500,
        capturePerfProfiles: [],
        maxPerfProfilesPerSession: 25,

        // Feature Flights
        shouldCaptureAssets: true,
        skipFlightControls: false,
        isFullVerboseLoggingEnabled: false,
        enablePrivacyGuard: false,
        enableSchemaEnforcement: false,
        turnOffCustomData: false,
        devToolsEnabled: false,
        trackEventFrequency: false,
        isDataWorkerEnabled: false,
        enableOfflineCaching: false,
        isDiagDatapointTracingPanelEnabled: false,
    };
}
